import { useEffect, useState } from "react";
import { Landmark, Upload, Loader2, FileCheck2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Input } from "./ui/input";

export default function BankFields({ bank, setBank, doc, setDoc, errors }) {
  const [busy, setBusy] = useState("");
  const [ifscErr, setIfscErr] = useState("");
  const set = (k) => (e) => {
    let v = e.target.value;
    if (k === "account_no" || k === "account_no2") v = v.replace(/\D/g, "").slice(0, 18);
    if (k === "ifsc") v = v.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 11);
    setBank({ ...bank, [k]: v });
  };

  useEffect(() => {
    setIfscErr("");
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(bank.ifsc)) { if (bank.bank_name) setBank((b) => ({ ...b, bank_name: "", branch: "" })); return; }
    setBusy("ifsc");
    api.get(`/geo/ifsc/${bank.ifsc}`).then((r) => setBank((b) => ({ ...b, bank_name: r.data.bank_name, branch: `${r.data.branch}, ${r.data.city}` })))
      .catch(() => { setBank((b) => ({ ...b, bank_name: "", branch: "" })); setIfscErr("IFSC not found. Please check the code on your passbook or cheque."); }).finally(() => setBusy(""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bank.ifsc]);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("File too large, max 5MB");
    setBusy("doc");
    try { const fd = new FormData(); fd.append("file", file); const r = await api.post("/auth/kyc-upload", fd); setDoc({ id: r.data.doc_id, name: file.name }); toast.success("Document uploaded"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const Err = ({ k }) => (errors[k] ? <p data-testid={`seller-${k}-error`} className="text-xs text-red-400 mt-1">{errors[k]}</p> : null);
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 space-y-3" data-testid="seller-bank-details">
      <p className="text-sm font-semibold flex items-center gap-2"><Landmark size={15} className="text-[#00f0ff]" />Bank details for payouts</p>
      <div><Input data-testid="seller-bank-holder-input" placeholder="Account holder name (as in bank)" value={bank.holder} onChange={set("holder")} className="h-11" /><Err k="holder" /></div>
      <div><Input data-testid="seller-bank-account-input" inputMode="numeric" name="bank-account-number" autoComplete="off" data-lpignore="true" style={{ WebkitTextSecurity: "disc" }} placeholder="Account number" value={bank.account_no} onChange={set("account_no")} className="h-11" /><Err k="account_no" /></div>
      <div><Input data-testid="seller-bank-account2-input" inputMode="numeric" name="bank-account-confirm" autoComplete="off" data-lpignore="true" placeholder="Re-enter account number" value={bank.account_no2} onChange={set("account_no2")} onPaste={(e) => e.preventDefault()} className="h-11" /><Err k="account_no2" /></div>
      <div className="relative"><Input data-testid="seller-bank-ifsc-input" placeholder="IFSC code, e.g. SBIN0000691" value={bank.ifsc} onChange={set("ifsc")} className="h-11 font-mono" />
        {busy === "ifsc" && <Loader2 size={15} className="absolute right-3 top-3.5 animate-spin text-slate-400" />}<Err k="ifsc" /></div>
      {ifscErr && <p data-testid="seller-ifsc-lookup-error" className="text-xs text-amber-400">{ifscErr}</p>}
      {bank.bank_name && <p data-testid="seller-bank-found" className="text-xs text-emerald-400">{bank.bank_name} · {bank.branch}</p>}
      {doc ? (
        <div data-testid="seller-kyc-doc-uploaded" className="flex items-center gap-2 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-3 py-2.5 text-sm">
          <FileCheck2 size={16} className="text-emerald-400 shrink-0" /><span className="truncate flex-1">{doc.name}</span>
          <button type="button" data-testid="seller-kyc-doc-remove" onClick={() => setDoc(null)} className="p-1 rounded-full hover:bg-white/10"><X size={14} /></button>
        </div>
      ) : (
        <label className="flex flex-col items-center gap-1 py-4 rounded-xl border border-dashed border-white/20 cursor-pointer hover:border-[#ff5500]/60 text-center">
          {busy === "doc" ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} className="text-[#ff5500]" />}
          <span className="text-sm font-semibold">Upload passbook front page or bank statement</span>
          <span className="text-[11px] text-slate-500">PDF, JPG or PNG · max 5MB · visible only to EAZYSHOPY admin</span>
          <input data-testid="seller-kyc-doc-input" type="file" accept="application/pdf,image/jpeg,image/png" hidden onChange={(e) => upload(e.target.files[0])} />
        </label>
      )}
      <Err k="doc" />
    </div>
  );
}
